import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { Task } from '@lit/task';
import { SlDialog, SlInput, SlSelect, SlSwitch } from '@shoelace-style/shoelace';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { api } from '../api.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { linkStyles } from './cssstyles.js';

type PlanConfig = {
  name: string,
  seats?: number,
  orgSlug?: string,
  trialDays?: number,
  maxDataRetentionDays?: number,
  maxStorageGB?: number,
  priceIdSeats?: string,
  priceIdBase: string,
  projectVisibilityType: WireTypes.ProjectVisibilityType,
  premiumFeatures: boolean,
  eliteFeatures: boolean,
}

@customElement('new-product-plan')
export class NewProductPlan extends LitElement {
  @property({ attribute: false }) orgSlug?: string;
  @property({ type: Boolean, attribute: 'hide-add-button' }) hideAddButton?: boolean;

  @query('#plan-name') private _fPlanName?: SlInput;
  @query('#annual') private _fAnnual?: SlSwitch;
  @query('#seats-count') private _fSeatsCount?: SlInput;
  @query('#data-retention') private _fDataRetention?: SlInput;
  @query('#trial-days') private _fTrialDays?: SlInput;
  @query('#seats-price') private _fPriceSeats?: SlSelect;
  @query('#base-price') private _fPriceBase?: SlSelect;
  @query('#max-storage') private _fMaxStorage?: SlInput;
  @query('#visibility-type') private _fVisibilityType?: SlSelect;
  @query('#premium-features') private _fPremiumFeatures?: SlSwitch;
  @query('#elite-features') private _fEliteFeatures?: SlSwitch;
  @query('#new-plan-dialog') private _newPlanDialog?: SlDialog;

  @state() private _editingPlan?: WireTypes.ProductPlan;
  @state() private _billing: WireTypes.BillingPeriod = 'month';
  @state() private _planName?: string;
  @state() private _seats?: number;
  @state() private _trialDays?: number;
  @state() private _maxDataRetentionDays?: number;
  @state() private _maxStorageGB?: number;
  @state() private _priceIdSeats?: string;
  @state() private _priceIdBase?: string;
  @state() private _projectVisibilityType?: WireTypes.ProjectVisibilityType;
  @state() private _premiumFeatures: boolean = false;
  @state() private _eliteFeatures: boolean = false;

  private _planPreviewTask = new Task(this, {
    args: () => [
      this.orgSlug, this._planName, this._seats, this._trialDays, this._maxDataRetentionDays,
      this._priceIdSeats, this._priceIdBase, this._maxStorageGB, this._projectVisibilityType,
      this._premiumFeatures, this._eliteFeatures,
    ] as const,
    task: async ([orgSlug, name, seats, trialDays, maxDataRetentionDays, priceIdSeats, priceIdBase, maxStorageGB, projectVisibilityType, premiumFeatures, eliteFeatures], { signal }) => {
      CHECK(name && projectVisibilityType && priceIdBase);
      const config: PlanConfig = {
        name,
        seats,
        orgSlug,
        trialDays,
        maxDataRetentionDays,
        priceIdSeats,
        priceIdBase,
        maxStorageGB,
        projectVisibilityType,
        premiumFeatures,
        // Elite features are only valid on custom (org-specific) plans.
        eliteFeatures: orgSlug ? eliteFeatures : false,
      };
      const plan = await api.productPlans.previewPlan.POST(config, { signal });
      return plan ? { plan, config } : undefined;
    },
  });

  private _pricesTask = new Task(this, {
    args: () => [],
    task: async ([], { signal }) => {
      return await api.productPlans.listPrices.GET(undefined, { signal });
    },
  });

  async showForNewPlan() {
    this._editingPlan = undefined;
    this._resetForm();
    this._newPlanDialog?.show();
  }

  async showForEdit(plan: WireTypes.ProductPlan) {
    this._editingPlan = plan;
    await this._populateForm(plan);
  }

  async showForDuplicate(plan: WireTypes.ProductPlan) {
    this._editingPlan = undefined;
    await this._populateForm(plan);
  }

  private async _populateForm(plan: WireTypes.ProductPlan) {
    this._billing = plan.billing;
    await this.updateComplete;
    await this._newPlanDialog?.show();
    await this.updateComplete;
    if (this._fPlanName) this._fPlanName.value = plan.name;
    if (this._fAnnual) this._fAnnual.checked = plan.billing === 'year';
    if (this._fSeatsCount) this._fSeatsCount.value = plan.seats?.toString() ?? '';
    if (this._fTrialDays) this._fTrialDays.value = plan.trialDays?.toString() ?? '';
    if (this._fDataRetention) this._fDataRetention.value = plan.maxDataRetentionDays.toString();
    if (this._fMaxStorage) this._fMaxStorage.value = plan.storage?.toString() ?? '';
    if (this._fPriceSeats) this._fPriceSeats.value = plan.priceIdSeats ?? '';
    if (this._fPriceBase) this._fPriceBase.value = plan.priceIdBase ?? '';
    if (this._fVisibilityType) this._fVisibilityType.value = plan.projectVisibilityType;
    if (this._fPremiumFeatures) this._fPremiumFeatures.checked = plan.premiumFeatures;
    if (this._fEliteFeatures) this._fEliteFeatures.checked = !!this.orgSlug && plan.eliteFeatures;
    this._updatePlanConfiguration();
  }

  private _resetForm() {
    this._billing = 'month';
    this._planName = undefined;
    this._seats = undefined;
    this._trialDays = undefined;
    this._maxDataRetentionDays = undefined;
    this._maxStorageGB = undefined;
    this._priceIdSeats = undefined;
    this._priceIdBase = undefined;
    this._projectVisibilityType = undefined;
    this._premiumFeatures = false;
    this._eliteFeatures = false;
    // Also reset uncontrolled Shoelace DOM inputs
    if (this._fPlanName) this._fPlanName.value = '';
    if (this._fAnnual) this._fAnnual.checked = false;
    if (this._fSeatsCount) this._fSeatsCount.value = '';
    if (this._fTrialDays) this._fTrialDays.value = '';
    if (this._fDataRetention) this._fDataRetention.value = '180';
    if (this._fMaxStorage) this._fMaxStorage.value = '';
    if (this._fPriceSeats) this._fPriceSeats.value = '';
    if (this._fPriceBase) this._fPriceBase.value = '';
    if (this._fVisibilityType) this._fVisibilityType.value = 'regular';
    if (this._fPremiumFeatures) this._fPremiumFeatures.checked = false;
    if (this._fEliteFeatures) this._fEliteFeatures.checked = false;
  }

  private async _updatePlanConfiguration() {
    const billingChanged = this._billing !== (this._fAnnual?.checked ? 'year' : 'month');
    this._billing = this._fAnnual?.checked ? 'year' : 'month';
    this._planName = this._fPlanName?.value;
    this._seats = this._fSeatsCount?.value ? parseInt(this._fSeatsCount?.value, 10) : undefined;
    this._trialDays = this._fTrialDays?.value ? parseInt(this._fTrialDays?.value, 10) : undefined;
    this._maxDataRetentionDays = this._fDataRetention?.value ? parseInt(this._fDataRetention?.value, 10) : undefined;
    this._maxStorageGB = this._fMaxStorage?.value ? parseInt(this._fMaxStorage?.value, 10) : undefined;
    this._priceIdSeats = (this._fPriceSeats?.value as string) || undefined;
    this._priceIdBase = (this._fPriceBase?.value as string) || undefined;
    this._projectVisibilityType = (this._fVisibilityType?.value as WireTypes.ProjectVisibilityType) || undefined;
    this._premiumFeatures = this._fPremiumFeatures?.checked ?? false;
    this._eliteFeatures = this._fEliteFeatures?.checked ?? false;
    if (billingChanged) {
      await this.updateComplete;
      this._priceIdSeats = (this._fPriceSeats?.value as string) || undefined;
      this._priceIdBase = (this._fPriceBase?.value as string) || undefined;
    }
  }

  render() {
    const previewPlan = this._planPreviewTask.value;
    const prices = this._pricesTask?.value;
    const seatPrices = prices?.seats.filter(price => price.billing === this._billing) ?? [];
    const basePrices = prices?.base.filter(price => price.billing === this._billing) ?? [];
    return html`
      ${this.hideAddButton ? nothing : html`<sl-icon-button size=small name=plus-circle @click=${() => this.showForNewPlan()}></sl-icon-button>`}
      <sl-dialog style="--width: 50vw;" id=new-plan-dialog label=${this._editingPlan ? 'Edit Product Plan' : 'New Product Plan'}>
        <v-box>
          ${this.orgSlug ? html`
            <fk-callout variant=tip>This product plan will be available <strong>ONLY</strong> to <strong>"${this.orgSlug}"</strong> organization
            </fk-callout>
          ` : nothing}
          <h-box style="gap: var(--sl-spacing-2x-large)">
            <v-box @sl-input=${this._updatePlanConfiguration}>
              <sl-switch id=annual>Annual</sl-switch>
              <sl-input id=plan-name label="Plan Name"></sl-input>
              <sl-input id=trial-days label="Trial Days" type="number" placeholder="Number" min=0 max=723>
                <div slot=suffix>days</div>
              </sl-input>
              <sl-select id=base-price label="Base price (flat)" value=${basePrices.at(0)?.priceId ?? ''} required>
                ${basePrices.map(price => html`
                  <sl-option value=${price.priceId}>${price.description}</sl-option>
                `)}
              </sl-select>
              <sl-input id=seats-count label="Seats Count" type="number" placeholder="Number" min=1 max=9999>
                <div slot=suffix>seats</div>
              </sl-input>
              <sl-select id=seats-price label="Price per seat (optional)" value="" clearable>
                ${seatPrices.map(price => html`
                  <sl-option value=${price.priceId}>${price.description}</sl-option>
                `)}
              </sl-select>
              <sl-input id=data-retention label="Data Retention" type="number" placeholder="Number" min=0 max=10000 value=180>
                <div slot=suffix>days</div>
              </sl-input>
              <sl-input id=max-storage label="Max Storage" type="number" placeholder="Unlimited" min=0 max=9999>
                <div slot=suffix>GB</div>
              </sl-input>
              <sl-select id=visibility-type label="Project Visibility Type" value=regular>
                <sl-option value="regular">Regular (Public, Private)</sl-option>
                <sl-option value="enterprise">Enterprise (Public, Private)</sl-option>
                <sl-option value="open_source">Open Source (Public only)</sl-option>
              </sl-select>
              <sl-switch id=premium-features>Premium features (Slack alerts, dedicated support)</sl-switch>
              <sl-switch id=elite-features ?disabled=${!this.orgSlug}>Elite features (Audit log)${this.orgSlug ? nothing : html`<div slot=help-text>Only custom plans (created for a specific organization) can enable elite features.</div>`}</sl-switch>
            </v-box>
            <product-plans-view
              no-enterprise
              no-subscribe
              .plans=${previewPlan ? [previewPlan.plan] : []}
            ></product-plans-view>
          </h-box>
        </v-box>

        <sl-button variant="primary" slot=footer ?disabled=${!previewPlan} @click=${async () => {
          if (!previewPlan)
            return;
          this._newPlanDialog?.hide();
          const plan = await api.productPlans.create.POST(previewPlan.config);
          if (!plan)
            return;
          if (this._editingPlan) {
            this.dispatchEvent(new CustomEvent<{oldPlan: WireTypes.ProductPlan, newPlan: WireTypes.ProductPlan}>('fk-edit-complete', {
              detail: { oldPlan: this._editingPlan, newPlan: plan },
            }));
          } else {
            this.dispatchEvent(new CustomEvent<WireTypes.ProductPlan>('fk-select', {
              detail: plan,
            }));
          }
          this._editingPlan = undefined;
        }}>${this._editingPlan ? 'save' : 'create'}</sl-button>
      </sl-dialog>
    `;
  }

  static styles = [linkStyles, css`
    product-plans {
      flex: auto;
      font-size: var(--sl-font-size-small);
    }
  `];
}
